import type { ApiErrorBody, LoginResponse } from '@pms/shared';

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
    public readonly details?: unknown,
  ) {
    super(message);
  }
  get warnings(): { code: string; message: string }[] {
    return (this.details as { warnings?: { code: string; message: string }[] } | undefined)?.warnings ?? [];
  }
}

let accessToken: string | null = null;
let refreshing: Promise<LoginResponse | null> | null = null;
type SessionListener = (s: LoginResponse | null) => void;
const listeners = new Set<SessionListener>();

export function onSession(fn: SessionListener): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

export function setSession(s: LoginResponse | null): void {
  accessToken = s?.accessToken ?? null;
  listeners.forEach((l) => l(s));
}

/** Single-flight refresh using the httpOnly refresh cookie. */
export function refreshSession(): Promise<LoginResponse | null> {
  refreshing ??= fetch('/api/auth/refresh', { method: 'POST', credentials: 'include' })
    .then(async (r) => (r.ok ? ((await r.json()) as LoginResponse) : null))
    .catch(() => null)
    .then((s) => {
      setSession(s);
      return s;
    })
    .finally(() => {
      refreshing = null;
    });
  return refreshing;
}

type Query = Record<string, string | number | boolean | undefined | null | string[]>;

function qs(query?: Query): string {
  if (!query) return '';
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(query)) {
    if (v === undefined || v === null || v === '') continue;
    if (Array.isArray(v)) v.forEach((x) => p.append(k, x));
    else p.set(k, String(v));
  }
  const s = p.toString();
  return s ? `?${s}` : '';
}

export async function api<T>(path: string, opts: { method?: string; body?: unknown; query?: Query } = {}): Promise<T> {
  const go = () =>
    fetch(`/api${path}${qs(opts.query)}`, {
      method: opts.method ?? 'GET',
      credentials: 'include',
      headers: {
        ...(opts.body !== undefined ? { 'content-type': 'application/json' } : {}),
        ...(accessToken ? { authorization: `Bearer ${accessToken}` } : {}),
      },
      body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
    });
  let res = await go();
  if (res.status === 401 && !path.startsWith('/auth/')) {
    if (await refreshSession()) res = await go();
  }
  if (res.status === 204) return undefined as T;
  const text = await res.text();
  const data = text ? JSON.parse(text) : undefined;
  if (!res.ok) {
    const e = (data ?? {}) as Partial<ApiErrorBody>;
    if (res.status === 401) setSession(null);
    throw new ApiError(res.status, e.code ?? 'HTTP_ERROR', e.message ?? res.statusText, e.details);
  }
  return data as T;
}

export const get = <T>(path: string, query?: Query) => api<T>(path, { query });

/** A file the API serves to signed-in staff only (a clock-in photo), as a Blob. */
export async function getBlob(path: string): Promise<Blob> {
  const go = () => fetch(`/api${path}`, { credentials: 'include', headers: accessToken ? { authorization: `Bearer ${accessToken}` } : {} });
  let res = await go();
  if (res.status === 401 && (await refreshSession())) res = await go();
  if (!res.ok) {
    const e = (await res.json().catch(() => ({}))) as Partial<ApiErrorBody>;
    throw new ApiError(res.status, e.code ?? 'HTTP_ERROR', e.message ?? res.statusText, e.details);
  }
  return res.blob();
}
/** Sends a file as the whole request body (a scan for a personnel file), its details in headers. */
export async function uploadFile<T>(path: string, file: Blob, headers: Record<string, string>): Promise<T> {
  const go = () =>
    fetch(`/api${path}`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'content-type': file.type, ...headers, ...(accessToken ? { authorization: `Bearer ${accessToken}` } : {}) },
      body: file,
    });
  let res = await go();
  if (res.status === 401 && (await refreshSession())) res = await go();
  const text = await res.text();
  const data = text ? (JSON.parse(text) as unknown) : undefined;
  if (!res.ok) {
    const e = (data ?? {}) as Partial<ApiErrorBody>;
    throw new ApiError(res.status, e.code ?? 'HTTP_ERROR', e.message ?? res.statusText, e.details);
  }
  return data as T;
}

/** Fetches a file from the API and saves it under the given name. */
export async function download(path: string, fileName: string): Promise<void> {
  const url = URL.createObjectURL(await getBlob(path));
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}
export const post = <T>(path: string, body?: unknown) => api<T>(path, { method: 'POST', body: body ?? {} });
export const patch = <T>(path: string, body: unknown) => api<T>(path, { method: 'PATCH', body });
export const put = <T>(path: string, body: unknown) => api<T>(path, { method: 'PUT', body });
export const del = <T>(path: string) => api<T>(path, { method: 'DELETE' });

export function errorMessage(e: unknown): string {
  if (e instanceof ApiError) {
    if (e.code === 'VALIDATION_FAILED' && Array.isArray(e.details)) {
      return (e.details as { path: string; message: string }[]).map((d) => `${d.path ? `${d.path}: ` : ''}${d.message}`).join('\n');
    }
    return e.message;
  }
  return e instanceof Error ? e.message : 'Something went wrong';
}
